import * as React from 'react';

/**
 * Assets
 */
import MyAppIcon from '../assets/app_white.svg';

/**
 * MUI Components
 */
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';

/**
 * Components
 */
import AppIcon from './AppIcon';

/**
 * Hooks
 */
import useLogout from '../hooks/useLogout';
import { useAppMenu } from '../contexts/NavBarContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function ResponsiveAppBar({ userInfo }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { doLogout } = useLogout();
    const { title: mainTitle } = useAppMenu();
    const [anchorElNav, setAnchorElNav] = React.useState(null);
    const [anchorElUser, setAnchorElUser] = React.useState(null);

    const today = new Date();
    let todayDate = today.toISOString().split('T')[0];

    let avatarUrl = 'https://ui-avatars.com/api/';
    let name = `${userInfo?.staff?.last_name || ''} ${userInfo?.staff?.first_name || ''}`.toUpperCase();

    // Only take the initials of each word
    name = name.split(' ').map(word => word[0]).join('');

    let avatarParams = {
        name: encodeURIComponent(name),
        length: 3,
        background: 'fff',
        color: 'b31117',
    };

    const urlObj = new URL(avatarUrl);
    const params = new URLSearchParams(urlObj.search);

    // Add params to the url
    Object.entries(avatarParams).forEach(([key, value]) => {
        params.set(key, value);
    });

    urlObj.search = params.toString();
    avatarUrl = urlObj.toString();

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogoutUser = () => {
        handleCloseUserMenu();
        doLogout();
    };

    const handleShowProfile = () => {
        handleCloseUserMenu();
        navigate('/profile');
    };

    const handleNavigation = (path) => {
        handleCloseNavMenu();

        // Handle special case for back office navigation
        if (path === '/back-office') {
            const baseUrl =
                new URL(window.location.href).protocol + '//' +
                new URL(window.location.href).hostname;
            console.info('Opening back office in new tab', baseUrl);
            window.open(`${baseUrl}/home`, '_gabinete');
            return;
        }

        navigate(path);
    };

    const pages = [
        { key: 'MyWorks', title: t('My works'), path: '/' },
        { key: 'MyReports', title: t('My reports'), path: '/my-reports' },
        { key: 'Notifications', title: t('Notifications'), path: '/notifications' },
        { key: 'VehiclesAlerts', title: t('Vehicles Alerts'), path: '/vehicles/alerts' },
        { key: 'BackOffice', title: t('Back Office'), path: '/back-office' },
    ];
    const settings = [
        {
            key: 'Profile',
            title: t('Profile'),
            handler: handleShowProfile,
        },
        {
            key: 'Logout',
            title: t('logout'),
            handler: handleLogoutUser,
        }
    ];

    return (
        <AppBar id="app-bar" position="sticky">
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    {/* <AppIcon
                        src={MyAppIcon}
                        alt="App Icon"
                        className="app-icon"
                        style={{
                            display: 'block',
                            width: '24px',
                            height: '24px',
                        }}
                    /> */}
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            mr: 2,
                            display: { xs: 'none', md: 'flex' },
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            // letterSpacing: '.3rem',
                            color: 'inherit',
                            textDecoration: 'none',
                        }}
                    >
                        {mainTitle} {todayDate}
                    </Typography>

                    <Box id="app-bar-menu" sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            {pages.map((page) => (
                                <MenuItem key={page.key} onClick={() => handleNavigation(page.path)}>
                                    <Typography textAlign="center">{page.title}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    <AppIcon
                        src={MyAppIcon}
                        alt="App Icon"
                        className="app-icon"
                        style={{
                            display: 'block',
                            width: '24px',
                            height: '24px',
                        }}
                    />
                    <Typography
                        variant="h5"
                        noWrap
                        component="div"
                        sx={{
                            mr: 2,
                            display: { xs: 'flex', md: 'none' },
                            flexGrow: 1,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            // letterSpacing: '.3rem',
                            color: 'inherit',
                            textDecoration: 'none',
                        }}
                    >
                        {mainTitle}
                    </Typography>
                    <Box id="app-bar-pages" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                        {pages.map((page) => (
                            <Button
                                id={`menu-pages-${page.key}`}
                                key={page.key}
                                onClick={() => handleNavigation(page.path)}
                                sx={{ my: 2, color: 'white', display: 'block' }}
                            >
                                {page.title}
                            </Button>
                        ))}
                    </Box>

                    <Box id="app-bar-user" sx={{ flexGrow: 0 }}>
                        <Tooltip title="Open settings">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <Avatar alt="PLACEHOLDER" src={avatarUrl} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-user"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            {settings.map((setting) => (
                                <MenuItem id={`menu-user-${setting.key}`} key={setting.key} onClick={setting.handler}>
                                    <Typography textAlign="center">{setting.title}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default ResponsiveAppBar;